import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import GlassCard from "../../General/GlassCard";
import { statusMeta, personName, timeAgo } from "../workspaceUtils";

function describe(activity) {
  const actor = personName(activity.actor) || "Alguien";
  if (activity.type === "created") {
    return `${actor} creó la tarea`;
  }
  if (activity.type === "status_change") {
    const from = activity.from?.label;
    const to = activity.to?.label || statusMeta(activity.to?.value).single;
    return from ? `${actor} pasó la tarea de "${from}" a "${to}"` : `${actor} cambió el estado a "${to}"`;
  }
  if (activity.type === "reassignment") {
    const from = activity.from?.name;
    const to = activity.to?.name || "otro usuario";
    return from ? `${actor} reasignó la tarea de ${from} a ${to}` : `${actor} asignó la tarea a ${to}`;
  }
  return `${actor} actualizó la tarea`;
}

export default function TaskActivityTimeline({ activities = [], embedded = false }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const lineCol = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";

  const ordered = [...activities].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at),
  );

  const list =
    ordered.length === 0 ? (
      <Typography variant="body2" sx={{ opacity: 0.6 }}>
        Todavía no hay movimientos registrados.
      </Typography>
    ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.6 }}>
          {ordered.map((activity, idx) => {
            const dotColor =
              activity.type === "status_change"
                ? statusMeta(activity.to?.value).color
                : theme.palette.primary.main;
            return (
              <Box key={activity.id ?? idx} sx={{ display: "flex", gap: 1.4 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pt: 0.4 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: dotColor, flexShrink: 0 }} />
                  {idx < ordered.length - 1 && (
                    <Box sx={{ width: 2, flex: 1, mt: 0.4, bgcolor: lineCol, minHeight: 18 }} />
                  )}
                </Box>
                <Box sx={{ pb: 0.4 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: 13.5, lineHeight: 1.4 }}>
                    {describe(activity)}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
                    {timeAgo(activity.created_at)}
                    {activity.created_at
                      ? ` · ${new Date(activity.created_at).toLocaleString()}`
                      : ""}
                  </Typography>
                  {activity.note && (
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, px: 1.2, py: 0.8, borderRadius: 2, bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)", whiteSpace: "pre-wrap" }}
                    >
                      {activity.note}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      );

  if (embedded) {
    return list;
  }

  return (
    <GlassCard>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <HistoryRoundedIcon sx={{ fontSize: 18, opacity: 0.7 }} />
        <Typography sx={{ fontWeight: 900, fontSize: 14 }}>Actividad de la tarea</Typography>
      </Box>
      {list}
    </GlassCard>
  );
}
